import React, { useState, useEffect } from "react";
import { Check } from "lucide-react";
import api from "@/lib/api";
import { loadStripe } from '@stripe/stripe-js';
import { useRouter, usePathname } from "next/navigation";
import { FREE_MODEL_NAMES, MODEL_ICON_BY_NAME, PAID_MODEL_NAMES } from "./model-config";
const PricingCard = ({ selected, onClick, plan, publicKey }) => {

  const pathname = usePathname();
  const router = useRouter();
  const handleSubscribe = async (plan) => {
    fbq('track', 'Subscribe', {
      value: plan.price, // 订阅金额
    });
    if (plan.name === "free") return;
    try {
      const response = await api.post("/stripe/checkout-session", {
        priceId: plan.priceId,
        success_url: `${window.location.href}?pay=success`,
        cancel_url: `${window.location.href}?pay=cancel`,
      });
      if (response.sessionId) {
        const stripe = await loadStripe(publicKey);
        const result = await stripe.redirectToCheckout({ sessionId: response.sessionId });
        if (result?.error) {
          console.log(result.error.message || 'Failed to start checkout process');
        }
      }
    } catch (error) {
      if (error.status == 401) {
        window.localStorage && window.localStorage.setItem('priceId', plan.priceId);
        setTimeout(() => {
          router.push(
            `/login?from=${encodeURIComponent(pathname)}`
          );
        }, 0);
      }
      console.error("Subscription failed:", error);
    }
  };
  return (
    <div
      onClick={onClick}
      className={`${
        selected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 hover:border-blue-200"
      } cursor-pointer rounded-2xl border-2 p-6 transition-all duration-200 flex flex-col h-full relative`}
    >
      {plan.discount && (
        <div className="absolute -right-6 -top-6 bg-emerald-400 text-white rounded-full p-6 text-3xl">
          {plan.discount}
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          {/* {plan.icon} */}
          {plan.name === "Free" ? <img src="https://cdn.heckai.weight-wave.com/free.png" className="w-8 h-8"/> : <img src="https://cdn.heckai.weight-wave.com/unlimited.png" className="w-8 h-8" />}
          <span className="font-semibold text-xl">{plan.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-2xl font-bold mb-1">{plan.price}</div>
          {plan.period && (
            <div className="font-bold text-sm">{plan.period}</div>
          )}
        </div>
        <div className="text-gray-500 text-sm">cancel at any time.</div>
      </div>

      <button
        className={`${
          selected
            ? "bg-blue-500 text-white"
            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        } w-full rounded-3xl py-3 transition-colors duration-200`}
        onClick={() => handleSubscribe(plan)}
      >
        {plan.buttonText}
      </button>

      <div className="flex-grow">
        {/* {plan.features.map((feature, index) => (
          <div key={index} className="flex items-start gap-2 mb-3">
            <Check className="w-5 h-5 text-blue-500 mt-0.5" />
            <span className="text-gray-600 text-sm">{feature}</span>
          </div>
        ))} */}
        {Object.values(plan.features).map((feature, index) => (
          <div key={index} className="flex items-start gap-2 my-3">
            {/* <Check className="w-5 h-5 text-blue-500 mt-0.5" /> */}
            <div>
              <div className="text-lg font-bold">{feature.title}</div>
              {Array.isArray(feature.items) ? feature.items.map((i)=> {
                return <div className="text-gray-600 flex py-1" key={i}>{renderImage(i)}{i}</div>
              }) : <div className="text-gray-600 pt-1">{feature.value}</div>}
            </div>
          </div>))}
      </div>
    </div>
  );
};

const renderImage = (i) => {
  const image = MODEL_ICON_BY_NAME[i];

  return image ? <img src={image} alt={`icon-${i}`} className="w-6 h-6 mr-2" /> : null;
};

const PricingPlans = () => {
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [plans, setPlans] = useState<any>(null);
  const [stripePublicKey, setStripePublicKey] = useState("");

  useEffect(() => {
    async function getPriceInfo() {
      const res = await api.get("/stripe/price-info");
      console.log(res);
      const stripe_public_key = res.stripe_public_key;
      setStripePublicKey(stripe_public_key);
      let defaultPlans = [
        {
          name: "Free",
          price: "US$0",
          icon: "✨",
          // features: [
          //   "ChatGPT-4o Mini",
          //   "Gemini 2.0 Flash",
          //   "DeepSeek R1",
          //   "DeepSeek V3",
          //   "Limited Chat：50 query/day",
          //   "Basic Search：5 Search query/day",
          //   "Ads：1 Ad/day",
          // ],
          features: {
            models: {
              title: "Basic Model",
              items: FREE_MODEL_NAMES
            },
            chat: {
              title: "Limited Chat",
              value: "50 query/day"
            },
            search: {
              title: "Basic Search",
              value: "5 Search query/day"
            },
            ads: {
              title: "Ads",
              value: "1 Ad/day"
            }
          },
          buttonText: "Current Plan",
        },
        {
          priceId: res.priceInfo.weekly.priceId,
          name: "Unlimited",
          price: `US$${res.priceInfo.weekly.price}`,
          period: "/week",
          icon: "∞",
          features: {
            models: {
              title: "Advanced Model",
              items: [...PAID_MODEL_NAMES, "And all basic models"]
            },
            chat: {
              title: "Unlimited Chat",
              value: "∞ Query/day"
            },
            search: {
              title: "Unlimited Search",
              value: "∞ Search/day"
            },
            ads: {
              title: "Ads",
              value: "No ads"
            },
            smartMode: {
              title: "Genius Mode",
              value: "upcoming in March 2025"
            }
          },
          buttonText: res.planInfo?.id == res.priceInfo.monthly.id ? 'Subscribed' : "Subscribe Now",
        },
        {
          priceId: res.priceInfo.monthly.priceId,
          name: "Unlimited",
          price: `US$${res.priceInfo.monthly.price}`,
          period: "/month",
          icon: "∞",
          discount: "-30%",
          // features: [
          //   "o3-mini High",
          //   "Claude Sonnet 3.5",
          //   "ChatGPT-4o",
          //   "Doubao 1.5 Pro",
          //   "And all basic models",
          //   "Unlimited Chat：∞ Query/day",
          //   "Unlimited Search：∞ Search/day",
          //   "Ads：No ads",
          //   "Genius Mode (upcoming in March 2025)"
          // ],
          features: {
            models: {
              title: "Advanced Model",
              items: [...PAID_MODEL_NAMES, "And all basic models"]
            },
            chat: {
              title: "Unlimited Chat",
              value: "∞ Query/day"
            },
            search: {
              title: "Unlimited Search",
              value: "∞ Search/day"
            },
            ads: {
              title: "Ads",
              value: "No ads"
            },
            smartMode: {
              title: "Genius Mode",
              value: "upcoming in March 2025"
            }
          },
          buttonText: res.planInfo?.id == res.priceInfo.monthly.id ? 'Subscribed' : "Subscribe Now",
        },
        {
          priceId: res.priceInfo.yearly.priceId,
          name: "Unlimited",
          price: `US$${res.priceInfo.yearly.price}`,
          period: "/year",
          icon: "∞",
          discount: "-60%",
          // features: [
          //   "o3-mini High",
          //   "Claude Sonnet 3.5",
          //   "ChatGPT-4o",
          //   "Doubao 1.5 Pro",
          //   "And all basic models",
          //   "Unlimited Chat：∞ Query/day",
          //   "Unlimited Search：∞ Search/day",
          //   "Ads：No ads",
          //   "Genius Mode (upcoming in March 2025)",
          // ],
          features: {
            models: {
              title: "Advanced Model",
              items: [...PAID_MODEL_NAMES, "And all basic models"]
            },
            chat: {
              title: "Unlimited Chat",
              value: "∞ Query/day"
            },
            search: {
              title: "Unlimited Search",
              value: "∞ Search/day"
            },
            ads: {
              title: "Ads",
              value: "No ads"
            },
            smartMode: {
              title: "Genius Mode",
              value: "upcoming in March 2025"
            }
          },
          buttonText: res.planInfo?.id == res.priceInfo.yearly.id ? 'Subscribed' : "Subscribe Now",
        },
      ];
      setPlans(defaultPlans);
    }
    getPriceInfo();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-2">
      <h1 className="text-center text-3xl font-bold mb-4">
        Upgrade For <span className="text-blue-500">Unlimited Uses</span>
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6 min-h-[600px]">
        {plans?.map((plan, index) => (
          <PricingCard
            key={index}
            plan={plan}
            publicKey={stripePublicKey}
            selected={selectedPlan === index}
            onClick={() => setSelectedPlan(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default PricingPlans;
